// src/services/smartInboxService.ts
// Smart Inbox — email processing + action execution.
// (Named SmartInboxService to avoid clashing with the existing Resend
//  transactional `emailService.ts`.) Adapted to the real DirectAuto schema:
//   deals uses deal_status / valuation_amount / paid_at / ppsr_status, id is UUID.

import { supabase } from '../lib/supabase'
import { transitionDealStatus } from '../lib/dealStatusService'
import {
  classifyEmail,
  shouldSuggest,
  extractVIN,
  extractRego,
  extractPrice,
  extractDealID,
  cleanEmailBody,
} from '../lib/smart-inbox/classifier'

export interface InboxEmail {
  id: number
  message_id: string
  from_address: string
  from_domain: string
  subject: string
  body_raw: string
  body_clean: string
  received_at: string
  predicted_action: string
  predicted_confidence: number
  rules_applied: string[]
  status: string
  override_flag: boolean
  processed_at?: string
  processed_by?: string
  deal_id?: string
  task_ref?: string
}

export type InboxAction = 'deal' | 'paid' | 'ppsr' | 'inspection' | 'finance' | 'manual'

export class SmartInboxService {
  /** Client-side ingest (the production path is the webhook Pages Function). */
  static async processIncomingEmail(payload: {
    message_id: string
    from: string
    to: string
    subject: string
    body_raw: string
    received_at: string
  }): Promise<{ status: string; email_id?: number; error?: string }> {
    try {
      const { data: existing } = await supabase
        .from('inbox_emails')
        .select('id')
        .eq('message_id', payload.message_id)
        .maybeSingle()
      if (existing) return { status: 'duplicate', email_id: existing.id }

      const fromDomain = this.extractDomain(payload.from)
      const bodyClean = cleanEmailBody(payload.body_raw)
      const classification = classifyEmail({
        subject: payload.subject,
        body: bodyClean,
        from_domain: fromDomain,
      })
      const initialStatus = shouldSuggest(classification.confidence) ? 'unprocessed' : 'manual_review'

      const { data, error } = await supabase
        .from('inbox_emails')
        .insert([
          {
            message_id: payload.message_id,
            from_address: payload.from,
            from_domain: fromDomain,
            subject: payload.subject,
            body_raw: payload.body_raw,
            body_clean: bodyClean,
            received_at: payload.received_at,
            predicted_action: classification.action,
            predicted_confidence: classification.confidence,
            rules_applied: classification.rulesMatched,
            status: initialStatus,
            override_flag: false,
          },
        ])
        .select('id')
        .single()

      if (error) return { status: 'error', error: error.message }
      // Note: we deliberately do NOT auto-execute. Side-effectful actions
      // (creating deals, marking paid) always require a human Accept in the UI.
      return { status: 'ok', email_id: data.id }
    } catch (error) {
      console.error('Smart Inbox ingest error:', error)
      return { status: 'error', error: (error as Error).message }
    }
  }

  /** Execute the (accepted) action for an email. */
  static async executeAction(
    emailId: number,
    action: InboxAction
  ): Promise<{ success: boolean; error?: string; result?: any }> {
    try {
      const { data: email, error: fetchError } = await supabase
        .from('inbox_emails')
        .select('*')
        .eq('id', emailId)
        .single()
      if (fetchError || !email) return { success: false, error: 'Email not found' }

      let result: any
      switch (action) {
        case 'deal':
          result = await this.createDealFromEmail(email as InboxEmail)
          break
        case 'paid':
          result = await this.markDealPaid(email as InboxEmail)
          break
        case 'ppsr':
          result = await this.updatePPSRStatus(email as InboxEmail)
          break
        case 'inspection':
          result = await this.createInspectionTask(email as InboxEmail)
          break
        case 'finance':
          result = await this.createFinanceTask(email as InboxEmail)
          break
        case 'manual':
          break
      }

      /* Processed means the system did the thing. Two of these actions only work out what a
       person still has to do, and calling those Processed told whoever pressed the button
       that an inspection had been booked when none had. They go to Manual Review, where
       somebody will actually see them. */
    const needsPerson = Boolean(result && result.needsPerson)
    const newStatus = action === 'manual' || needsPerson ? 'manual_review' : 'processed'
      const { data: userRes } = await supabase.auth.getUser()
      await supabase
        .from('inbox_emails')
        .update({
          status: newStatus,
          processed_at: new Date().toISOString(),
          processed_by: userRes.user?.id,
        })
        .eq('id', emailId)

      return { success: true, result }
    } catch (error) {
      console.error('Smart Inbox action error:', error)
      return { success: false, error: (error as Error).message }
    }
  }

  /** Create a draft deal from an email. Required deal fields are placeholder-
   *  filled where the email doesn't yield them, so the sourcer can complete it
   *  in the pipeline. */
  private static async createDealFromEmail(email: InboxEmail): Promise<any> {
    const vin = extractVIN(email.body_raw)
    const rego = extractRego(email.body_raw)
    const askingPrice = extractPrice(email.body_raw)
    const sellerName = this.extractSenderName(email.from_address)
    const { year, make, model } = this.extractVehicle(email.body_raw + ' ' + email.subject)

    const { data: userRes } = await supabase.auth.getUser()
    const userId = userRes.user?.id
    if (!userId) return { warning: 'Not signed in — cannot create deal' }

    const notesParts = [
      'Created from Smart Inbox.',
      rego ? `Rego: ${rego}` : null,
      `Seller email: ${email.from_address}`,
      '',
      email.body_clean,
    ].filter((x) => x !== null)

    const { data, error } = await supabase
      .from('deals')
      .insert([
        {
          user_id: userId,
          vehicle_make: make || 'Unknown',
          vehicle_model: model || 'Unknown',
          vehicle_year: year || new Date().getFullYear(),
          vehicle_vin: vin || null,
          seller_name: sellerName,
          seller_email: this.extractSenderEmail(email.from_address),
          valuation_amount: askingPrice || 0,
          deal_status: 'sourced',
          notes: notesParts.join('\n'),
        },
      ])
      .select('id')
      .single()
    if (error) throw error

    await supabase.from('inbox_emails').update({ deal_id: data.id }).eq('id', email.id)
    return { deal_id: data.id, vin, rego, asking_price: askingPrice, needsReview: !vin || !make }
  }

  private static async markDealPaid(email: InboxEmail): Promise<any> {
    const dealId = extractDealID(email.body_raw)
    if (!dealId) return { warning: 'Could not extract a deal reference from the email' }

    // Best-effort match on notes/vin (our deal ids are UUIDs, not DA-YYYY-NNNN).
    const { data: deal } = await supabase
      .from('deals')
      .select('id, deal_status')
      .or(`notes.ilike.%${dealId}%,vehicle_vin.ilike.%${dealId}%`)
      .maybeSingle()
    if (!deal) return { warning: `Deal ${dealId} not found — link manually` }

    /*
     * Through the same door as every other route to Paid.
     *
     * This used to write `deal_status: 'paid'` straight onto the deal. That skipped the PPSR
     * payment gate, the dual-approval preconditions and the status log entirely - an email
     * that looked like a remittance could mark a car paid with no PPSR check against its VIN,
     * which the Licensed Seller spec section 4 forbids without qualification: "payment release
     * must independently verify a cleared PPSR check". Found by the 14 September spec audit.
     *
     * A refusal is returned as a warning rather than thrown: the inbox should say why it did
     * not act, not fail the whole email.
     */
    const moved = await transitionDealStatus(
      deal.id,
      String(deal.deal_status || 'approved_for_payment') as never,
      'paid' as never,
      undefined,
      undefined,
      'Smart Inbox — remittance email ' + dealId,
    )
    if (!moved.success) {
      await supabase.from('inbox_emails').update({ deal_id: deal.id }).eq('id', email.id)
      return {
        deal_id: deal.id,
        warning: 'Linked to the deal, but it was not marked paid: ' + (moved.message || 'the transition was refused'),
      }
    }

    await supabase.from('inbox_emails').update({ deal_id: deal.id }).eq('id', email.id)
    return { deal_id: deal.id }
  }

  private static async updatePPSRStatus(email: InboxEmail): Promise<any> {
    const dealId = extractDealID(email.body_raw)
    const body = email.body_raw.toLowerCase()
    const isClear = body.includes('clear') && !body.includes('not clear') && !body.includes('encumber')

    if (!dealId) return { warning: 'Could not extract a deal reference from the email', ppsr: isClear ? 'clear' : 'encumbered' }

    const { data: deal } = await supabase
      .from('deals')
      .select('id')
      .or(`notes.ilike.%${dealId}%,vehicle_vin.ilike.%${dealId}%`)
      .maybeSingle()
    if (!deal) return { warning: `Deal ${dealId} not found — link manually` }

    const { error } = await supabase
      .from('deals')
      .update({
        ppsr_status: isClear ? 'clear' : 'encumbered',
        ppsr_check_date: new Date().toISOString(),
      })
      .eq('id', deal.id)
    if (error) throw error

    await supabase.from('inbox_emails').update({ deal_id: deal.id }).eq('id', email.id)
    return { deal_id: deal.id, ppsr_status: isClear ? 'clear' : 'encumbered' }
  }

  /* Reads what the email is asking for. It does NOT book an inspection - booking one
     emails a vendor and texts a seller, and that is a decision a person makes.

     It used to write a reference string onto the email and mark it Processed, which read
     to whoever pressed the button as "an inspection has been booked". None had been. It
     now hands the email to a person, with what it managed to work out. */
  private static async createInspectionTask(email: InboxEmail): Promise<any> {
    const dealId = extractDealID(email.body_raw)
    const vin = extractVIN(email.body_raw)
    return {
      type: 'inspection',
      needsPerson: true,
      email_id: email.id,
      deal_ref: dealId,
      vin,
      note:
        'Inspection request' +
        (dealId ? ' for ' + dealId : vin ? ' for VIN ' + vin : '') +
        '. No booking has been made - book it from the deal workspace.',
    }
  }

  /* Reads the payout figure. It does NOT raise a finance task, because there is nowhere
     in this system for one to live yet - and marking the email Processed said one had
     been raised. Hands it to a person instead. */
  private static async createFinanceTask(email: InboxEmail): Promise<any> {
    const amount = extractPrice(email.body_raw)
    const dealId = extractDealID(email.body_raw)
    return {
      type: 'finance_payout',
      needsPerson: true,
      email_id: email.id,
      amount,
      deal_ref: dealId,
      note:
        'Finance payout figure' +
        (amount ? ' of ' + amount : '') +
        (dealId ? ' for ' + dealId : '') +
        '. Nothing has been raised automatically - record it against the deal by hand.',
    }
  }

  static async logTrainingCorrection(
    emailId: number,
    predictedAction: string,
    actualAction: string,
    confidence: number,
    notes?: string
  ): Promise<{ success: boolean; error?: string }> {
    const { data: userRes } = await supabase.auth.getUser()
    const { error } = await supabase.from('training_corrections').insert([
      {
        email_id: emailId,
        predicted_action: predictedAction,
        actual_action: actualAction,
        predicted_confidence: confidence,
        corrected_by: userRes.user?.id,
        notes,
      },
    ])
    if (error) return { success: false, error: error.message }
    await supabase.from('inbox_emails').update({ override_flag: true }).eq('id', emailId)
    return { success: true }
  }

  private static extractDomain(email: string): string {
    const match = email.match(/@([a-z0-9.-]+)/i)
    return match ? match[1] : 'unknown'
  }

  private static extractSenderName(email: string): string {
    const raw = String(email || '').trim()
    const display = raw.match(/^"?([^"<]+?)"?\s*<[^>]+>$/)
    if (display && display[1] && !display[1].includes('@')) return display[1].trim()
    const local = String(this.extractSenderEmail(raw)).split('@')[0] || ''
    const words = local.replace(/[._+-]+/g, ' ').replace(/[0-9]+/g, ' ').trim()
    if (!words) return 'Unknown Seller'
    return words.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  private static extractSenderEmail(email: string): string {
    const raw = String(email || '').trim()
    const angled = raw.match(/<([^>]+)>/)
    return (angled ? angled[1] : raw).trim()
  }

  private static extractVehicle(text: string): { year?: number; make?: string; model?: string } {
    const m = text.match(
      /\b(19|20)(\d{2})\s+(toyota|honda|mazda|ford|hyundai|subaru|mitsubishi|nissan|volkswagen|vw|kia|bmw|mercedes|audi|tesla|holden|isuzu|lexus|jeep)\s*([a-z0-9-]+)?/i
    )
    if (!m) return {}
    return {
      year: parseInt(`${m[1]}${m[2]}`, 10),
      make: m[3].charAt(0).toUpperCase() + m[3].slice(1).toLowerCase(),
      model: m[4] ? m[4].charAt(0).toUpperCase() + m[4].slice(1) : undefined,
    }
  }

  static async loadUnprocessedEmails(limit = 30): Promise<InboxEmail[]> {
    const { data, error } = await supabase
      .from('inbox_emails')
      .select('*')
      .eq('status', 'unprocessed')
      .order('received_at', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  }

  static async loadManualReviewEmails(limit = 30): Promise<InboxEmail[]> {
    const { data, error } = await supabase
      .from('inbox_emails')
      .select('*')
      .eq('status', 'manual_review')
      .order('received_at', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  }

  static async loadProcessedEmails(limit = 30): Promise<InboxEmail[]> {
    const { data, error } = await supabase
      .from('inbox_emails')
      .select('*')
      .eq('status', 'processed')
      .order('processed_at', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  }

  static async loadTrainingLog(limit = 50): Promise<any[]> {
    const { data, error } = await supabase
      .from('training_corrections')
      .select(
        `id, email_id, predicted_action, actual_action, predicted_confidence, corrected_at,
         inbox_emails(subject, from_address, received_at)`
      )
      .order('corrected_at', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  }
}
